import { useMutate, UseMutateProps } from 'restful-react'
import { IShipment, ICarrierApplication, ICarrierCompliance } from 'types'
import { TRACKING_METHOD_TYPE } from 'types/tracking'
import { FP_ENDPOINTS } from 'apps/fleet-planner/config/endpoints'
import {
  TSourceChannel,
  TSourceSegment,
} from 'apps/fleet-planner/context/track-conversion-source/types'
import { BidResponse } from 'apps/fleet-planner/types'
import { getApiUrl } from './apiHelpers'
import { callApi, ENDPOINTS } from './callApi'

/**
 * Fetches single load from MP
 * @method GET
 */
export const fetchMarketplaceLoad = (
  id: string
): Promise<{
  shipment: IShipment
}> => {
  return callApi({
    method: 'GET',
    endpoint: ENDPOINTS.fleetPlannerShipments,
    id,
    params: {
      includes: [
        'auto_appts',
        'backhaul_eligible_lanes',
        'driver_tracking_information',
        'load_board_posts',
        'project44_metadata',
        'reschedule_requests',
        'shipper_insurance_requirement',
      ],
    },
  })
}

/**
 * Fetches a carrier application by id
 * @method GET
 */
export const fetchMarketplaceCarrierApplication = (
  motorCarrierDotNumber: number,
  motorCarrierMcNumber: number | null
): Promise<{
  carrierApplication: ICarrierApplication
}> => {
  return callApi({
    method: 'GET',
    endpoint: ENDPOINTS.fleetPlannerCarriers,
    params: {
      motorCarrierDotNumber,
      motorCarrierMcNumber,
    },
  })
}

/**
 * Check for carrier compliance given a shipment
 * @method GET
 */
export const checkMarketplaceShipmentCarrierCompliance = (
  id: number,
  motorCarrierDotNumber: number,
  motorCarrierMcNumber: number | null
): Promise<ICarrierCompliance> => {
  return callApi({
    method: 'GET',
    endpoint: ENDPOINTS.fleetPlannerShipments,
    id,
    group: 'carrier_insurance_match',
    params: {
      motorCarrierDotNumber,
      motorCarrierMcNumber,
    },
  })
}

export type TSignRateConfirmationBody = Partial<{
  carrierSignature: string
  dispatcherId: number | string | null
  driverId: number | string | null
  motorCarrierDotNumber: number
  motorCarrierMcNumber: number | null
  quickPayEnabled: boolean
  trackingMethod: TRACKING_METHOD_TYPE
  trailerNumber: string | null
  truckNumber: string | null
  vin: string | null
  // Track conversion source
  sourceChannel: TSourceChannel
  sourceSegment: TSourceSegment
}>

/**
 * Updates a single rate confirmation
 * @method PUT
 */
export const updateMarketplaceLoadRateCon = (
  body: TSignRateConfirmationBody,
  id: string
): Promise<{
  shipment: IShipment
}> => {
  return callApi({
    method: 'PUT',
    id,
    endpoint: ENDPOINTS.fleetPlannerShipments,
    group: 'sign_rate_confirmation',
    body,
  })
}

export interface IMarketplaceBid {
  id?: number
  amount?: string
  carrierContactPhone: string
  carrierContactPhoneExt?: string
  shipmentId: number
}

export interface IDeleteMarketplaceBid {
  message?: string
  errors?: string[]
}

export interface ICreateMarketplaceBidRequestBody {
  bid: IMarketplaceBid
}

export type TUseCreateMarketplaceBidProps = Omit<
  UseMutateProps<IMarketplaceBid, void, void, ICreateMarketplaceBidRequestBody, void>,
  'path' | 'verb'
>

/**
 * Creates a bid
 */
export const useCreateMarketplaceBid = (props: TUseCreateMarketplaceBidProps) =>
  useMutate<IMarketplaceBid, void, void, ICreateMarketplaceBidRequestBody, void>(
    'POST',
    ENDPOINTS.fleetPlannerBids,
    {
      base: getApiUrl('MARKETPLACE'),
      ...props,
    }
  )

export interface IUpdateMarketplaceBidRequestBody {
  bid: Partial<IMarketplaceBid>
}

export interface IUpdateMarketplaceBidPathParams {
  id: number
}

export type TUseUpdateMarketplaceBidProps = Omit<
  UseMutateProps<
    IMarketplaceBid,
    void,
    void,
    IUpdateMarketplaceBidRequestBody,
    IUpdateMarketplaceBidPathParams
  >,
  'path' | 'verb'
>

/**
 * Updates a bid
 */
export const useUpdateMarketplaceBid = (props: TUseUpdateMarketplaceBidProps) =>
  useMutate<
    IMarketplaceBid,
    void,
    void,
    IUpdateMarketplaceBidRequestBody,
    IUpdateMarketplaceBidPathParams
  >('PUT', ({ id }) => `${ENDPOINTS.fleetPlannerBids}/${id}`, {
    base: getApiUrl('MARKETPLACE'),
    ...props,
  })

/**
 * Gets a bid
 * @method GET
 */
export const getMarketplaceBid = (bidId: number): Promise<{ bid: BidResponse }> => {
  return callApi({
    method: 'GET',
    endpoint: `${FP_ENDPOINTS.carrierBids}/${bidId}`,
  })
}

/**
 * Deletes a bid
 * (internally sets declined reason to deleted by user)
 * @method DELETE
 */
export const deleteMarketplaceBid = (bidId: string): Promise<IDeleteMarketplaceBid> => {
  return callApi({
    method: 'DELETE',
    id: bidId,
    endpoint: ENDPOINTS.fleetPlannerBids,
  })
}
